import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getPageSeo, type PageSeo } from "@/data/pageSeoContent";

interface Props {
  /** Override the route lookup. Defaults to current pathname. */
  path?: string;
  /** Pass content directly instead of using the registry. */
  content?: PageSeo;
}

/**
 * Renders an SEO-rich content block:
 *  - H2 intro + paragraphs
 *  - "What your child will learn" list (optional)
 *  - FAQ accordion + FAQPage JSON-LD
 *  - Conclusion paragraph
 *
 * Designed to be dropped just above <Footer /> on learning pages.
 */
const SeoContentSection = ({ path, content }: Props) => {
  const location = useLocation();
  const seo = content ?? getPageSeo(path ?? location.pathname);
  if (!seo) return null;

  const faqJsonLd =
    seo.faqs && seo.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: seo.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: f.a,
            },
          })),
        }
      : null;

  return (
    <section
      aria-labelledby="seo-section-heading"
      className="bg-white border-t border-cyan-100"
    >
      {faqJsonLd && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(faqJsonLd)}
          </script>
        </Helmet>
      )}

      <div className="container mx-auto px-4 py-10 md:py-14 max-w-3xl">
        <h2
          id="seo-section-heading"
          className="text-2xl md:text-3xl font-fredoka font-bold text-cyan-800 mb-4"
        >
          {seo.introHeading}
        </h2>

        <div className="space-y-3 text-gray-700 text-base md:text-[17px] leading-relaxed">
          {seo.intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {seo.learn && seo.learn.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg md:text-xl font-fredoka font-semibold text-cyan-700 mb-2">
              What your child will learn
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-gray-700">
              {seo.learn.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {seo.faqs && seo.faqs.length > 0 && (
          <div id="faq" className="mt-8 scroll-mt-24">
            <h2 className="text-2xl md:text-3xl font-fredoka font-bold text-cyan-800 mb-4">
              {seo.faqHeading ?? "Frequently Asked Questions"}
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {seo.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-fredoka text-base md:text-lg text-gray-900">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {seo.conclusion && (
          <p className="mt-8 text-gray-700 italic border-l-4 border-cyan-300 pl-4">
            {seo.conclusion}
          </p>
        )}
      </div>
    </section>
  );
};

export default SeoContentSection;
